import { type SetupStep2Input, setupStep2Schema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { type UseFormSetValue, useForm } from 'react-hook-form';
import { DemoPrefillButton } from '../../demo/components/DemoPrefillButton';
import { SETUP_DEFAULT_LOCALE, SETUP_DEFAULT_TIME_ZONE } from '../constants/setup-default-locale';
import { useSetupWizardStore } from '../store/setup-wizard.store';

function applyImmutableFrRegionalDefaults(setValue: UseFormSetValue<SetupStep2Input>) {
  const { countryCode, currencyCode, languageCode } = SETUP_DEFAULT_LOCALE;
  setValue('countryCode', countryCode, { shouldDirty: false, shouldValidate: true });
  setValue('currencyCode', currencyCode, { shouldDirty: false, shouldValidate: true });
  setValue('languageCode', languageCode, { shouldDirty: false, shouldValidate: true });
  setValue('timeZone', SETUP_DEFAULT_TIME_ZONE, { shouldDirty: false, shouldValidate: true });
}

function FieldSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200/90 bg-white/70 px-5 py-6 shadow-sm sm:px-6">
      <div>
        <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {title}
        </h3>
        {description ? (
          <p className="mt-2 text-[0.88rem] leading-relaxed text-slate-600">{description}</p>
        ) : null}
      </div>
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

export function SetupStep2Form() {
  const { step2, setStep, setStep2 } = useSetupWizardStore();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SetupStep2Input>({
    resolver: zodResolver(setupStep2Schema),
    defaultValues: {
      name: step2.name ?? '',
      tradeName: step2.tradeName ?? '',
      legalForm: step2.legalForm ?? '',
      registrationNumber: step2.registrationNumber ?? '',
      taxId: step2.taxId ?? '',
      countryCode: SETUP_DEFAULT_LOCALE.countryCode,
      currencyCode: SETUP_DEFAULT_LOCALE.currencyCode,
      languageCode: SETUP_DEFAULT_LOCALE.languageCode,
      timeZone: SETUP_DEFAULT_TIME_ZONE,
      addressLine1: step2.addressLine1 ?? '',
      addressLine2: step2.addressLine2 ?? '',
      postalCode: step2.postalCode ?? '',
      city: step2.city ?? '',
      country: step2.country ?? '',
      phone: step2.phone ?? '',
      email: step2.email ?? '',
      website: step2.website ?? '',
      logoUrl: step2.logoUrl ?? '',
      industry: step2.industry ?? '',
      numberOfEmployees: step2.numberOfEmployees,
      foundingYear: step2.foundingYear,
    },
  });

  return (
    <form
      className="space-y-10"
      noValidate
      onSubmit={handleSubmit((data) => {
        setStep2({
          ...data,
          countryCode: SETUP_DEFAULT_LOCALE.countryCode,
          currencyCode: SETUP_DEFAULT_LOCALE.currencyCode,
          languageCode: SETUP_DEFAULT_LOCALE.languageCode,
          timeZone: SETUP_DEFAULT_TIME_ZONE,
        });
      })}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            Étape 2 — Votre entreprise
          </h2>
          <p className="mt-2 text-[0.9rem] leading-relaxed text-slate-600">
            Complétez la fiche de l’organisation. Pays, devise, langue et fuseau horaire
            (Europe/Paris) sont fixés automatiquement pour la France métropolitaine : pas besoin de
            les saisir.
          </p>
        </div>
        <DemoPrefillButton
          context="setup-step2"
          onPrefill={(data) => {
            for (const [k, raw] of Object.entries(data)) {
              if (
                k === 'countryCode' ||
                k === 'currencyCode' ||
                k === 'languageCode' ||
                k === 'timeZone'
              ) {
                continue;
              }
              setValue(k as keyof SetupStep2Input, raw as never, { shouldDirty: true });
            }
            applyImmutableFrRegionalDefaults(setValue);
          }}
        />
      </div>

      {/* Champs gardés dans le DOM pour react-hook-form + validation Zod */}
      <input type="hidden" {...register('countryCode')} />
      <input type="hidden" {...register('currencyCode')} />
      <input type="hidden" {...register('languageCode')} />
      <input type="hidden" {...register('timeZone')} />
      <input type="hidden" {...register('logoUrl')} />

      <FieldSection
        title="Identification légale"
        description="Dénominations et identifiants administratifs."
      >
        <div>
          <label htmlFor="name" className="app-label">
            Raison sociale
          </label>
          <input id="name" className="app-input" {...register('name')} />
          {errors.name && <p className="text-error mt-2">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="tradeName" className="app-label">
            Nom commercial
          </label>
          <input id="tradeName" className="app-input" {...register('tradeName')} />
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="legalForm" className="app-label">
              Forme juridique
            </label>
            <input id="legalForm" className="app-input" {...register('legalForm')} />
          </div>
          <div>
            <label htmlFor="registrationNumber" className="app-label">
              SIRET de l’entreprise
            </label>
            <input
              id="registrationNumber"
              className="app-input"
              placeholder="14 chiffres"
              {...register('registrationNumber')}
            />
            {errors.registrationNumber && (
              <p className="text-error mt-2">{errors.registrationNumber.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="taxId" className="app-label">
              Numéro de TVA
            </label>
            <input
              id="taxId"
              className="app-input"
              placeholder="ex. FR + 11 caractères"
              {...register('taxId')}
            />
            {errors.taxId && <p className="text-error mt-2">{errors.taxId.message}</p>}
          </div>
          <div>
            <label htmlFor="industry" className="app-label">
              Code APE (NAF)
            </label>
            <input
              id="industry"
              className="app-input"
              placeholder="ex. 62.01Z"
              maxLength={100}
              autoComplete="off"
              {...register('industry')}
            />
            {errors.industry && <p className="text-error mt-2">{errors.industry.message}</p>}
          </div>
        </div>
      </FieldSection>

      <FieldSection
        title="Adresse et localisation"
        description="Siège ou adresse principale utilisée comme référence."
      >
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="addressLine1" className="app-label">
              Adresse
            </label>
            <input id="addressLine1" className="app-input" {...register('addressLine1')} />
          </div>
          <div>
            <label htmlFor="addressLine2" className="app-label">
              Complément d’adresse
            </label>
            <input id="addressLine2" className="app-input" {...register('addressLine2')} />
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          <div>
            <label htmlFor="postalCode" className="app-label">
              Code postal
            </label>
            <input id="postalCode" className="app-input" {...register('postalCode')} />
          </div>
          <div>
            <label htmlFor="city" className="app-label">
              Ville
            </label>
            <input id="city" className="app-input" {...register('city')} />
          </div>
          <div>
            <label htmlFor="country" className="app-label">
              Pays (libellé)
            </label>
            <input id="country" className="app-input" {...register('country')} />
          </div>
        </div>
      </FieldSection>

      <FieldSection title="Contact" description="Coordonnées générales de l’organisation.">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="phone" className="app-label">
              Téléphone
            </label>
            <input id="phone" type="tel" className="app-input" {...register('phone')} />
          </div>
          <div>
            <label htmlFor="email" className="app-label">
              E-mail
            </label>
            <input id="email" type="email" className="app-input" {...register('email')} />
          </div>
        </div>
      </FieldSection>

      <FieldSection
        title="Présence en ligne et taille"
        description="Site institutionnel et effectifs de l’organisation, si vous les renseignez."
      >
        <div>
          <label htmlFor="website" className="app-label">
            Site web
          </label>
          <input id="website" className="app-input" {...register('website')} />
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="numberOfEmployees" className="app-label">
              Nombre d’employés
            </label>
            <input
              id="numberOfEmployees"
              type="number"
              min={0}
              className="app-input"
              {...register('numberOfEmployees', { valueAsNumber: true })}
            />
          </div>
          <div>
            <label htmlFor="foundingYear" className="app-label">
              Année de création
            </label>
            <input
              id="foundingYear"
              type="number"
              min={1800}
              max={2100}
              className="app-input"
              {...register('foundingYear', { valueAsNumber: true })}
            />
          </div>
        </div>
      </FieldSection>

      <div className="flex justify-between gap-4">
        <button type="button" className="btn-muted" onClick={() => setStep(1)}>
          Retour
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          Suivant : sites
        </button>
      </div>
    </form>
  );
}
